<template>
    <div class="row q-no-gutters">
        <div v-if="isCompanyVisible" :class="['col-12', cityShow ? 'col-sm-3' : 'col-sm-6', 'q-pa-xs']">
            <CompanyAutoComplete v-bind:company_id="companyComputed" @company_selected="companySelected"
                :clear="clearCompanyAutocomplete" @clear="onClearCompany" />
        </div>

        <div v-if="isRouteVisible" :class="['col-12', cityShow ? 'col-sm-3' : 'col-sm-6', 'q-pa-xs']">
            <RouteAutocompleteComponent v-bind:route_id="routeComputed" :company_id="companyComputed"
                @route_selected="routeSelected" :clear="clearRouteAutocomplete" @clear="onClearRoute" />
        </div>

        <div v-if="isVehicleVisible" :class="['col-12', cityShow ? 'col-sm-3' : 'col-sm-2', 'q-pa-xs']">
            <VehicleAutocompleteComponent v-bind:vehicle_id="vehicleComputed" :company_id="companyComputed"
                @vehicle_selected="vehicleSelected" :clear="clearVehicleAutocomplete" @clear="onClearVehicle" />
        </div>

        <div v-if="isAdministratorVisible" :class="['col-12', cityShow ? 'col-sm-3' : 'col-sm-2', 'q-pa-xs']">
            <AdministratorAutocompleteComponent :administrator_id="administratorComputed"
                @administrator_selected="administratorSelected" :clear="clearAdministratorAutocomplete"
                :label="labelAdministrator" @clear="onClearAdministrator" :company_id="companyComputed" />
        </div>
        <div v-if="isActiveVisible" :class="['col-12', cityShow ? 'col-sm-2' : 'col-sm-2', 'q-pa-sm']">
            <div class="dropdown-container">
                <q-btn-dropdown color="primary" label="Activo / Inactivo">
                    <q-list>
                        <q-item tag="label" v-ripple>
                            <q-item-section side top>
                                <q-checkbox v-model="active" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>Activos</q-item-label>
                            </q-item-section>
                        </q-item>

                        <q-item tag="label" v-ripple>
                            <q-item-section side top>
                                <q-checkbox v-model="inactive" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>Inactivos</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
            </div>
        </div>
    </div>

</template>

<script lang="ts">
import { computed, defineComponent, reactive, toRefs } from 'vue';

import { OptionDto } from '@/dtos';
import CompanyAutoComplete from "@/ui/components/autocompletes/fleet-management/CompanyAutocompleteComponent.vue";
import RouteAutocompleteComponent from "@/ui/components/autocompletes/routes/RouteAutocompleteComponent.vue";
import VehicleAutocompleteComponent from "@/ui/components/autocompletes/fleet-management/VehicleAutocompleteComponent.vue";
import AdministratorAutocompleteComponent from "@/ui/components/autocompletes/auth/AdministratorAutocompleteComponent.vue";

interface Props {
    cityShow?: boolean,
    company_id?: number,
    route_id?: number,
    vehicle_id?: number,
    administrator_id?: number,
    isCompanyVisible?: boolean,
    isRouteVisible?: boolean,
    isVehicleVisible?: boolean,
    isAdministratorVisible?: boolean,
    isActiveVisible?: boolean,
    labelAdministrator?: string
}

export default defineComponent({
    name: 'FilterSelectComponent',
    components: {
        CompanyAutoComplete,
        RouteAutocompleteComponent,
        VehicleAutocompleteComponent,
        AdministratorAutocompleteComponent
    },
    props: {
        cityShow: {
            type: Boolean,
            default: true,
        },
        isCompanyVisible: {
            type: Boolean,
            default: true,
        },
        isRouteVisible: {
            type: Boolean,
            default: true,
        },
        isVehicleVisible: {
            type: Boolean,
            default: true,
        },
        isAdministratorVisible: {
            type: Boolean,
            default: true,
        },
        isActiveVisible: {
            type: Boolean,
            default: true,
        },
        labelAdministrator: {
            type: String,
            default: 'Administrator',
        },
        company_id: { type: Number },
        route_id: { type: Number },
        vehicle_id: { type: Number },
        administrator_id: { type: Number }
    },
    setup(props: Props, { emit }) {
        const config = reactive({
            clearCompanyAutocomplete: false,
            clearRouteAutocomplete: false,
            clearVehicleAutocomplete: false,
            clearAdministratorAutocomplete: false,
            active: true,
            inactive: false,
        })

        const companyComputed = computed({
            get() { return props.company_id },
            set(newValue) {
                emit('update:company_id', newValue);
            }
        });

        const companySelected = (option: OptionDto) => {
            companyComputed.value = (option) ? option.value : 0;
        }

        const routeComputed = computed({
            get() { return props.route_id },
            set(newValue) {
                emit('update:route_id', newValue)
            }
        });

        const routeSelected = (option: OptionDto) => {
            if (option) {
                routeComputed.value = option.value;
            }
        }

        const vehicleComputed = computed({
            get() { return props.vehicle_id },
            set(newValue) {
                emit('update:vehicle_id', newValue)
            }
        });

        const vehicleSelected = (option: OptionDto) => {
            if (option) {
                vehicleComputed.value = option.value;
            }
        }

        const administratorComputed = computed({
            get() { return props.administrator_id },
            set(newValue) {
                emit('update:administrator_id', newValue)
            }
        });

        const administratorSelected = (option: OptionDto) => {
            if (option) {
                administratorComputed.value = option.value;
            }
        }

        const onClearCompany = () => {
            emit('update:company_id', 0);
        }

        const onClearRoute = () => {
            emit('update:route_id', 0);
        }

        const onClearVehicle = () => {
            emit('update:vehicle_id', 0);
        }

        const onClearAdministrator = () => {
            emit('update:administrator_id', 0);
        }

        return {
            ...toRefs(config),
            companyComputed,
            routeComputed,
            vehicleComputed,
            administratorComputed,
            companySelected,
            routeSelected,
            vehicleSelected,
            administratorSelected,
            onClearCompany,
            onClearRoute,
            onClearVehicle,
            onClearAdministrator
        }
    }
})
</script>